import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../context/AuthContext';
import { 
  getBookingDetail, 
  getBookingMessages, 
  sendMessage,
  getPaymentStatus,
  initiatePayment
} from '../../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Calendar,
  Users,
  MapPin,
  Send,
  Loader2,
  Check,
  CreditCard,
  MessageCircle
} from 'lucide-react';

export default function BookingDetail() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);

  // Check for payment session
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [bookingRes, messagesRes] = await Promise.all([
          getBookingDetail(bookingId),
          getBookingMessages(bookingId, { limit: 50 })
        ]);
        
        setBooking(bookingRes.data);
        setMessages(messagesRes.data.items || []);
      } catch (error) {
        console.error('Failed to load booking:', error);
        toast.error('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [bookingId]);

  // Poll payment status if session_id is present
  useEffect(() => {
    if (sessionId && booking?.status !== 'paid') {
      const pollPayment = async (attempts = 0) => {
        if (attempts >= 5) {
          toast.info('Payment status check timed out. Please check back later.');
          return;
        }

        setPollingPayment(true);
        try {
          const response = await getPaymentStatus(sessionId);
          
          if (response.data.payment_status === 'paid') {
            setBooking(prev => ({ ...prev, status: 'paid' }));
            toast.success('Payment successful! Your booking is confirmed.');
            setPollingPayment(false);
            return;
          }

          // Continue polling
          setTimeout(() => pollPayment(attempts + 1), 2000);
        } catch (error) {
          console.error('Payment status check failed:', error);
          setPollingPayment(false);
        }
      };

      pollPayment();
    }
  }, [sessionId, booking?.status]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      const response = await sendMessage(bookingId, newMessage);
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handlePayNow = async () => {
    try {
      const response = await initiatePayment(bookingId);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      completed: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-slate-100 text-slate-500',
      refunded: 'bg-purple-100 text-purple-700'
    };
    return variants[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-20">
        <h2 className="font-heading text-xl font-semibold mb-4">Booking not found</h2>
        <Link to="/app/bookings">
          <Button className="btn-primary">Back to Bookings</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="booking-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {booking.property_title}
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <Badge className={getStatusBadge(booking.status)}>
              {booking.status}
            </Badge>
            {pollingPayment && (
              <span className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking payment...
              </span>
            )}
          </div>
        </div>
        
        {booking.status === 'approved' && (
          <Button onClick={handlePayNow} className="btn-accent">
            <CreditCard className="w-4 h-4 mr-2" />
            Pay Now
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Booking Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Check-in</p>
                    <p className="font-medium">{format(new Date(booking.check_in), 'EEE, MMM d, yyyy')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Check-out</p>
                    <p className="font-medium">{format(new Date(booking.check_out), 'EEE, MMM d, yyyy')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Guests</p>
                    <p className="font-medium">{booking.guests_count} guest{booking.guests_count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-medium">{booking.property_city}</p>
                  </div>
                </div>
              </div>

              {/* Address (only shown after payment) */}
              {booking.status === 'paid' && booking.address && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Property Address</h4>
                    <p className="text-slate-600">
                      {booking.address.line1}<br />
                      {booking.address.line2 && <>{booking.address.line2}<br /></>}
                      {booking.property_city}, FL {booking.address.postal_code}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Messages */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                {messages.length > 0 ? (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id}
                        className={`flex ${msg.sender_user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] rounded-lg p-3 ${
                          msg.sender_user_id === user?.id 
                            ? 'bg-primary text-white' 
                            : 'bg-slate-100 text-slate-900'
                        }`}>
                          <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                          <p>{msg.message_text}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">
                    No messages yet. Start the conversation!
                  </p>
                )}
              </ScrollArea>

              <form onSubmit={handleSendMessage} className="flex gap-2 mt-4">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button 
                  type="submit" 
                  disabled={sendingMessage || !newMessage.trim()}
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Price Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="font-heading">Price Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-slate-600">
                <span>{formatPrice(booking.nightly_price_snapshot)} × {booking.nights} nights</span>
                <span>{formatPrice(booking.nightly_price_snapshot * booking.nights)}</span>
              </div>
              
              {booking.cleaning_fee_snapshot > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Cleaning fee</span>
                  <span>{formatPrice(booking.cleaning_fee_snapshot)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-slate-600">
                <span>Service fee</span>
                <span>{formatPrice(booking.guest_fee_snapshot)}</span>
              </div>
              
              {booking.taxes_snapshot > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Taxes</span>
                  <span>{formatPrice(booking.taxes_snapshot)}</span>
                </div>
              )}
              
              <Separator />
              
              <div className="flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span>{formatPrice(booking.total_charged)}</span>
              </div>

              {booking.status === 'paid' && (
                <div className="flex items-center gap-2 text-green-600 mt-4 p-3 bg-green-50 rounded-lg">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Payment confirmed</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
